import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fileHubApi } from '@/features/file-hub/api';
import {
  fetchResearchQuestions,
  fetchResearchRecommendations,
  getResearchDraft,
  listClientAnswers,
  lookupClientAnswers,
  saveResearchDraft,
  submitResearchRequest,
} from '../api';
import {
  answerText,
  answerValues,
  applyPreliminaryAccept,
  applyPreliminaryClear,
  extractClarificationAnswers,
  findPreliminaryConflicts,
  findQuestionBySuffix,
  getDetailsQuestions,
  getMaterialsFileQuestions,
  getPreliminaryQuestions,
  getPreliminaryStatus,
  getVisibleQuestions,
  groupQuestionsByBlock,
  isFileQuestionType,
  isQuestionRequired,
  listMissingRequiredAnswers,
  mapAnswersForServiceSwitch,
  MATERIALS_ACCEPT,
  normalizeQuestionOptions,
  seedAnswersFromPreliminary,
  validateMaterialFiles,
  validateRequiredAnswers,
  type PreliminaryDecision,
} from '../researchWizardUtils';
import type {
  CatalogResolved,
  ClientAnswerItem,
  GatewayService,
  ResearchClarification,
  ResearchQuestion,
  ResearchStep,
  ResearchSubmitResult,
} from '../types';

interface ResearchWizardOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  catalog: CatalogResolved | null;
  activeRole: string;
  initialDirectionId?: string | null;
  initialServiceId?: string | null;
  initialStep?: ResearchStep;
}

const MULTI_TYPES = new Set([
  'multi',
  'multiselect',
  'checkbox',
  'entity_multi',
  'author_multi',
  'service_multi',
]);

function asStringArray(value: unknown): string[] {
  return answerValues(value);
}

async function readFileForHub(file: File) {
  const content_base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Не удалось прочитать файл'));
    reader.readAsDataURL(file);
  });
  const checksum = `sha256_${file.name.replace(/[^a-zA-Z0-9]/g, '_')}_${file.size}`;
  return {
    original_name: file.name,
    display_name: file.name.replace(/\.[^/.]+$/, ''),
    mime_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
    checksum,
    duplicate_action: 'create_new' as const,
    content_preview: `Research draft attachment: ${file.name}`,
    content_base64,
  };
}

export const ResearchWizardOverlay: React.FC<ResearchWizardOverlayProps> = ({
  isOpen,
  onClose,
  onBack,
  catalog,
  activeRole,
  initialDirectionId = null,
  initialServiceId = null,
  initialStep,
}) => {
  const hasStageZero = catalog?.has_stage_zero ?? false;
  const isClient = activeRole === 'CLIENT';
  const displayRole =
    activeRole === 'SYSTEM_OWNER'
      ? 'Владелец системы'
      : activeRole === 'OPERATIONS_PARTNER'
        ? 'Операционный партнёр'
        : activeRole === 'CLIENT'
          ? 'Клиент'
          : activeRole === 'AUTHOR'
            ? 'Автор / соавтор'
            : activeRole === 'RESEARCH_PARTNER' || activeRole === 'MENTOR_PARTNER'
              ? 'Научный партнёр'
              : activeRole;

  const [step, setStep] = useState<ResearchStep>(initialStep ?? (hasStageZero ? 0 : 1));
  const [startMode, setStartMode] = useState<'SCRATCH' | 'PULL_CLIENT'>('SCRATCH');
  const [selectedDirection, setSelectedDirection] = useState<string | null>(initialDirectionId);
  const [selectedService, setSelectedService] = useState<GatewayService | null>(null);

  useEffect(() => {
    if (!initialServiceId || !catalog) return;
    const found = catalog.services.find((item) => item.service_id === initialServiceId);
    if (found) setSelectedService(found);
  }, [catalog, initialServiceId]);
  const [selectedClientCategory, setSelectedClientCategory] = useState<string | null>(null);

  const [reqCodeInput, setReqCodeInput] = useState('');
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  const [isLookupError, setIsLookupError] = useState(false);
  const [showClientAnswersModal, setShowClientAnswersModal] = useState(false);
  const [clientAnswersList, setClientAnswersList] = useState<ClientAnswerItem[]>([]);
  const [importedProvenance, setImportedProvenance] = useState<any | null>(null);

  const [catalogQuestions, setCatalogQuestions] = useState<ResearchQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, unknown>>({});
  const [reviewId, setReviewId] = useState<string | undefined>();
  const [reviewMode, setReviewMode] = useState<string>('');
  const [reviewPending, setReviewPending] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewRetry, setReviewRetry] = useState(0);
  const [clarifications, setClarifications] = useState<ResearchClarification[]>([]);
  const [clarificationAnswers, setClarificationAnswers] = useState<Record<string, string>>({});
  const [recommendations, setRecommendations] = useState<
    Array<{ rec_id: string; title: string; description: string; confirmed: boolean }>
  >([]);

  const [validationError, setValidationError] = useState<string | null>(null);

  const [files, setFiles] = useState<Array<{ file_id: string; name: string; size: number; mime?: string; source?: string; assigned_to?: string }>>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [preliminaryAnswers, setPreliminaryAnswers] = useState<Array<any>>([]);
  const [prelimDecisions, setPrelimDecisions] = useState<Record<string, PreliminaryDecision>>({});
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  const [declarationConfirmed, setDeclarationConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<ResearchSubmitResult | null>(null);
  const [saveStatus, setSaveStatus] = useState<string>('Сохранено - 10:13');
  const [draftId, setDraftId] = useState<string>('new');
  const [idempotencyKey, setIdempotencyKey] = useState<string>(
    () => `idemp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  );
  const draftLoadedRef = useRef(false);

  const firstLogicalStep: ResearchStep = hasStageZero ? 0 : 1;

  const visibleQuestions = useMemo(
    () => getVisibleQuestions(catalogQuestions, questionAnswers),
    [catalogQuestions, questionAnswers]
  );

  const questionBlocks = useMemo(
    () => groupQuestionsByBlock(getDetailsQuestions(visibleQuestions)),
    [visibleQuestions]
  );

  const materialsQuestions = useMemo(
    () => getMaterialsFileQuestions(visibleQuestions),
    [visibleQuestions]
  );

  const preliminaryQuestions = useMemo(
    () => getPreliminaryQuestions(catalogQuestions),
    [catalogQuestions]
  );

  const preliminaryConflicts = useMemo(
    () => findPreliminaryConflicts(catalogQuestions, questionAnswers, prelimDecisions),
    [catalogQuestions, questionAnswers, prelimDecisions]
  );

  const missingRequired = useMemo(
    () => listMissingRequiredAnswers(catalogQuestions, questionAnswers, files),
    [catalogQuestions, questionAnswers, files]
  );

  const selectedDirectionMeta = useMemo(
    () => (catalog?.directions || []).find((d) => d.direction_id === selectedDirection) || null,
    [catalog?.directions, selectedDirection]
  );

  const directionServices = useMemo(() => {
    if (!selectedDirection) return [];
    return (catalog?.services || []).filter((s) => s.direction_id === selectedDirection);
  }, [catalog?.services, selectedDirection]);

  const projectTitle = useMemo(() => {
    const titleQ = findQuestionBySuffix(catalogQuestions, '_q_title');
    if (titleQ) return answerText(questionAnswers[titleQ.id]);
    const firstText = visibleQuestions.find(
      (q) => q.type === 'text' || q.type === 'textarea'
    );
    if (firstText) return answerText(questionAnswers[firstText.id]);
    return answerText(questionAnswers.project_title);
  }, [catalogQuestions, questionAnswers, visibleQuestions]);



  const loadQuestionsForService = useCallback(
    (serviceId: string, seedAnswers?: Record<string, unknown>) => {
      setQuestionsLoading(true);
      fetchResearchQuestions(serviceId, activeRole)
        .then((res) => {
          const questions = normalizeQuestionOptions(res.questions);
          setCatalogQuestions(questions);
          setPrelimDecisions({});
          setQuestionAnswers((prev) => {
            const pruned = mapAnswersForServiceSwitch(questions, seedAnswers ?? prev);
            return seedAnswersFromPreliminary(questions, pruned);
          });
        })
        .catch(() => setCatalogQuestions([]))
        .finally(() => setQuestionsLoading(false));
    },
    [activeRole]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';

    if (!draftLoadedRef.current) {
      draftLoadedRef.current = true;
      getResearchDraft(activeRole)
        .then((d) => {
          if (d && d.id !== 'new') {
            setDraftId(d.id);
            if (d.idempotency_key) {
              setIdempotencyKey(d.idempotency_key);
            }
            if (d.answers) {
              setQuestionAnswers(d.answers);
              setClarificationAnswers(extractClarificationAnswers(d.answers));
            }
            if (d.files) setFiles(d.files);
            setClarificationAnswers(d.clarification_answers || {});
            if (d.service_id && catalog?.services) {
              const foundSrv = catalog.services.find((s) => s.service_id === d.service_id);
              if (foundSrv) {
                setSelectedService(foundSrv);
                setSelectedDirection(foundSrv.direction_id);
                loadQuestionsForService(d.service_id, d.answers || {});
              }
            } else if (d.direction_id) {
              setSelectedDirection(d.direction_id);
            }
            if (d.step !== undefined && (hasStageZero || d.step > 0)) {
              setStep(d.step);
            }
            if (d.recommendations?.length) {
              setRecommendations(d.recommendations);
            }
          }
        })
        .catch(() => {});
    }

    return () => {
      if (!document.querySelector('.rg-overlay')) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, activeRole, hasStageZero, catalog, loadQuestionsForService]);

  useEffect(() => {
    if ((step !== 4 && !(step === 5 && !reviewId)) || !selectedService?.service_id) return;
    let live = true;
    setReviewPending(true);
    setReviewError('');
    setReviewId(undefined);
    fetchResearchRecommendations(
      { service_id: selectedService.service_id, answers: questionAnswers, files, role: activeRole }, activeRole
    ).then((res) => {
      if (!live) return;
      setRecommendations(res.recommendations.map((text, idx) => ({ rec_id: `rec_${idx}`, title: text, description: '', confirmed: true })));
      setClarifications(res.clarifications);
      setReviewId(res.review_id);
      setReviewMode(res.mode || 'rules');
    }).catch((error) => {
      if (live) setReviewError(error instanceof Error ? error.message : 'Не удалось проверить заявку');
    }).finally(() => { if (live) setReviewPending(false); });
    return () => { live = false; };
  }, [step, selectedService?.service_id, activeRole, questionAnswers, files, reviewRetry]);


  if (!isOpen) return null;

  const handleAutosave = (newStep: ResearchStep) => {
    setSaveStatus('Сохранение...');
    saveResearchDraft(
      {
        step: newStep,
        start_mode: startMode,
        service_id: selectedService?.service_id ?? null,
        direction_id: selectedService?.direction_id ?? selectedDirection ?? null,
        answers: questionAnswers,
        review_id: reviewId,
        clarification_answers: clarificationAnswers,
        files,
        recommendations,
        idempotency_key: idempotencyKey,
      },
      activeRole
    )
      .then((res) => {
        setDraftId(res.id);
        const now = new Date();
        const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(
          now.getMinutes()
        ).padStart(2, '0')}`;
        setSaveStatus(`Сохранено - ${timeStr}`);
      })
      .catch(() => {
        setSaveStatus('Ошибка сохранения');
      });
  };

  const handleExitAndContinueLater = () => {
    // Save draft and return to Hub (Продолжить restores context) — do not remount legacy create URL.
    handleAutosave(step);
    onBack();
  };

  const handleLookupClient = async () => {
    if (!reqCodeInput.trim()) return;
    setLookupMessage('Поиск ответов клиента...');
    setIsLookupError(false);
    try {
      const res = await lookupClientAnswers(reqCodeInput, activeRole);
      if (res.found && res.record) {
        setLookupMessage(res.message);
        setImportedProvenance(res.record.source_provenance);
        setPreliminaryAnswers(res.record.answers);
        setStartMode('PULL_CLIENT');
        const legacyAnswers: Record<string, unknown> = { project_title: res.record.title };
        for (const a of res.record.answers) {
          legacyAnswers[a.field_id] = a.answer;
        }
        setQuestionAnswers((prev) => ({ ...prev, ...legacyAnswers }));
      } else {
        setIsLookupError(true);
        setLookupMessage(res.message);
      }
    } catch (err: any) {
      setIsLookupError(true);
      setLookupMessage(err.message || 'Ошибка поиска ответов');
    }
  };

  const handleOpenClientAnswersModal = async () => {
    setShowClientAnswersModal(true);
    try {
      const res = await listClientAnswers(20, activeRole);
      setClientAnswersList(res.items);
    } catch {}
  };

  const handleSelectClientRecord = (record: ClientAnswerItem) => {
    setImportedProvenance(record.source_provenance);
    setPreliminaryAnswers(record.answers);
    setStartMode('PULL_CLIENT');
    const legacyAnswers: Record<string, unknown> = { project_title: record.title };
    for (const a of record.answers) {
      legacyAnswers[a.field_id] = a.answer;
    }
    setQuestionAnswers((prev) => ({ ...prev, ...legacyAnswers }));
    setShowClientAnswersModal(false);
    setLookupMessage(`Импортированы ответы из ${record.request_number}`);
  };

  const handleSelectService = (srv: GatewayService) => {
    const switching =
      Boolean(selectedService) && selectedService!.service_id !== srv.service_id;
    setSelectedService(srv);
    setSelectedDirection(srv.direction_id);
    if (switching) {
      setFiles([]);
      setClarifications([]);
      setClarificationAnswers({});
      setRecommendations([]);
    }
    loadQuestionsForService(srv.service_id, questionAnswers);
    setStep(2);
    handleAutosave(2);
  };

  const handleSelectClientCategory = (catId: string) => {
    setSelectedClientCategory(catId);
    const cat = catalog?.client_categories.find((c) => c.category_id === catId);
    if (cat) {
      const srv: GatewayService = {
        service_id: cat.target_service_id,
        display_code: 'CLIENT',
        direction_id: cat.target_direction_id,
        title: cat.title,
        description: cat.description,
      };
      setSelectedService(srv);
      setSelectedDirection(cat.target_direction_id);
      loadQuestionsForService(srv.service_id, { ...questionAnswers, project_title: cat.title });
    }
    setStep(2);
    handleAutosave(2);
  };

  const handleQuestionChange = (questionId: string, value: unknown) => {
    setQuestionAnswers((prev) => ({ ...prev, [questionId]: value }));
    if (preliminaryQuestions.some((q) => q.id === questionId)) {
      setPrelimDecisions((prev) => ({ ...prev, [questionId]: 'edited' }));
    }
    setValidationError(null);
  };

  const handleAcceptPreliminary = (q: ResearchQuestion) => {
    setQuestionAnswers((prev) => applyPreliminaryAccept(q, prev));
    setPrelimDecisions((prev) => ({ ...prev, [q.id]: 'accepted' }));
    setValidationError(null);
  };

  const handleClearPreliminary = (q: ResearchQuestion) => {
    setQuestionAnswers((prev) => applyPreliminaryClear(q, prev));
    setPrelimDecisions((prev) => ({ ...prev, [q.id]: 'cleared' }));
    setValidationError(null);
  };

  const handleRestorePreliminary = (q: ResearchQuestion) => {
    setQuestionAnswers((prev) => applyPreliminaryAccept(q, prev));
    setPrelimDecisions((prev) => ({ ...prev, [q.id]: 'accepted' }));
    setValidationError(null);
  };

  const canProceedFromStep = (currentStep: ResearchStep): string | null => {
    if (currentStep === 1) {
      if (!isClient && !selectedService) {
        return selectedDirection
          ? 'Выберите услугу, чтобы продолжить'
          : 'Выберите направление, затем услугу';
      }
      if (isClient && !selectedService) {
        return 'Выберите категорию запроса';
      }
    }
    if (currentStep === 2) {
      return validateRequiredAnswers(catalogQuestions, questionAnswers, files, {
        includeFiles: false,
      });
    }
    if (currentStep === 4) {
      if (reviewPending) return 'Дождитесь проверки заявки';
      if (reviewError || !reviewId) return 'Повторите проверку заявки';
      const unanswered = clarifications.find((q) => q.blocking && !clarificationAnswers[q.id]?.trim());
      if (unanswered) return `Ответьте на обязательное уточнение: ${unanswered.question}`;
    }
    return null;
  };

  const handleGoBack = () => {
    setValidationError(null);
    if (step === firstLogicalStep) {
      if (step === 1 && !isClient && selectedDirection) {
        setSelectedDirection(null);
        return;
      }
      // BACK FROM RESEARCH: flush draft, then return to Create Request Hub only.
      // Never navigate to legacy /app/requests/new/* or browser history layers.
      handleAutosave(step);
      onBack();
      return;
    }
    if (step === 1 && !isClient && selectedDirection) {
      setSelectedDirection(null);
      return;
    }
    const prev = (step - 1) as ResearchStep;
    setStep(prev);
    handleAutosave(prev);
  };

  const handleNextStep = () => {
    const err = canProceedFromStep(step);
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(null);
    const next = (step + 1) as ResearchStep;
    setStep(next);
    handleAutosave(next);
  };

  const uploadMaterialFiles = async (selected: File[], questionId?: string) => {
    const validation = validateMaterialFiles(selected);
    if (validation) {
      setValidationError(validation);
      return;
    }
    setIsUploadingFiles(true);
    setValidationError(null);
    try {
      const payloads = await Promise.all(selected.map(readFileForHub));
      const response = await fileHubApi.uploadBatch({
        category: 'projects',
        folder: 'Заявки / Research',
        entity_type: 'research_draft',
        entity_id: draftId !== 'new' ? draftId : idempotencyKey,
        entity_title: projectTitle || selectedService?.title || 'Research draft',
        scope_id: activeRole || 'SYSTEM_OWNER',
        comment: questionId
          ? `Вложение к вопросу ${questionId}`
          : 'Вложение к научно-исследовательской заявке',
        enable_ai_indexing: false,
        files: payloads,
      });
      const uploaded = response.results
        .filter((r) => r.status === 'ok' || r.file_id)
        .map((r) => ({
          file_id: r.file_id,
          name: r.original_name || r.display_name,
          size: selected.find((f) => f.name === r.original_name)?.size || 0,
          source: 'file-hub',
          assigned_to: questionId,
        }));
      if (uploaded.length === 0) {
        throw new Error('Не удалось загрузить файлы');
      }
      setFiles((prev) => [...prev, ...uploaded]);
      if (questionId) {
        setQuestionAnswers((prev) => {
          const existing = asStringArray(prev[questionId]);
          return {
            ...prev,
            [questionId]: [...existing, ...uploaded.map((f) => f.file_id)],
          };
        });
      }
    } catch (err: any) {
      setValidationError(err?.message || 'Ошибка загрузки файлов в файловое хранилище');
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    questionId?: string
  ) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const selected = Array.from(e.target.files);
    e.target.value = '';
    await uploadMaterialFiles(selected, questionId);
  };

  const handleAssignFile = (fileId: string, questionId: string) => {
    setFiles((prev) =>
      prev.map((f) => (f.file_id === fileId ? { ...f, assigned_to: questionId || undefined } : f))
    );
    if (!questionId) return;
    setQuestionAnswers((prev) => {
      const next = { ...prev };
      for (const q of materialsQuestions) {
        const ids = asStringArray(next[q.id]).filter((id) => id !== fileId);
        next[q.id] = ids;
      }
      const existing = asStringArray(next[questionId]);
      if (!existing.includes(fileId)) {
        next[questionId] = [...existing, fileId];
      }
      return next;
    });
  };

  const handleRemoveFile = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
    setQuestionAnswers((prev) => {
      const next = { ...prev };
      for (const [qid, value] of Object.entries(next)) {
        if (!Array.isArray(value)) continue;
        const filtered = value.filter((v) => String(v) !== fileId);
        if (filtered.length !== value.length) {
          next[qid] = filtered;
        }
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!selectedService) return;
    if (isSubmitting) return;
    const reviewProblem = canProceedFromStep(4);
    if (reviewProblem) { setValidationError(reviewProblem); return; }
    const requiredErr = validateRequiredAnswers(catalogQuestions, questionAnswers, files);
    if (requiredErr) {
      setValidationError(requiredErr);
      return;
    }
    if (preliminaryConflicts.length > 0) {
      setValidationError(
        `Устраните конфликты предварительных ответов (${preliminaryConflicts.length}) перед созданием заявки`
      );
      return;
    }
    if (!declarationConfirmed) {
      setValidationError('Подтвердите декларацию перед созданием заявки');
      return;
    }
    setValidationError(null);
    setIsSubmitting(true);
    try {
      const res = await submitResearchRequest(
        {
          draft_id: draftId,
          review_id: reviewId,
          clarification_answers: clarificationAnswers,
          service_id: selectedService.service_id,
          direction_id: selectedService.direction_id,
          title: projectTitle || selectedService.title,
          deadline: answerText(
            questionAnswers[
              findQuestionBySuffix(catalogQuestions, '_q_deadline')?.id || 'deadline'
            ]
          ),
          is_urgent: ['URGENT', 'HIGH', 'CRITICAL'].includes(
            answerText(
              questionAnswers[
                findQuestionBySuffix(catalogQuestions, '_q_priority')?.id || 'priority'
              ]
            ).toUpperCase()
          ),
          answers: questionAnswers,
          files,
          recommendations,
          declaration_confirmed: declarationConfirmed,
          idempotency_key: idempotencyKey,
        },
        activeRole
      );
      setSubmitResult(res);
    } catch (err: any) {
      alert(err.message || 'Ошибка при отправке заявки');
    } finally {
      setIsSubmitting(false);
    }
  };

  const visibleSteps = hasStageZero
    ? [
        { num: 0 as ResearchStep, title: 'Способ начала' },
        { num: 1 as ResearchStep, title: 'Услуга' },
        { num: 2 as ResearchStep, title: 'Сведения' },
        { num: 3 as ResearchStep, title: 'Материалы' },
        { num: 4 as ResearchStep, title: 'Предварительные ответы' },
        { num: 5 as ResearchStep, title: 'Проверка' },
      ]
    : [
        { num: 1 as ResearchStep, title: 'Услуга' },
        { num: 2 as ResearchStep, title: 'Сведения' },
        { num: 3 as ResearchStep, title: 'Материалы' },
        { num: 4 as ResearchStep, title: 'Предварительные ответы' },
        { num: 5 as ResearchStep, title: 'Проверка' },
      ];

  const inputClass =
    'w-full px-3.5 py-2 rounded-xl text-xs bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-amber-400';

  const renderQuestionField = (q: ResearchQuestion) => {
    const value = questionAnswers[q.id] ?? '';
    const fieldId = `rg-q-${q.id}`;
    const descriptionId = q.help_text || q.comment ? `${fieldId}-help` : undefined;
    const type = String(q.type || 'text').toLowerCase();

    if (type === 'group') {
      return (
        <p className="text-[11px] text-slate-500 italic">
          Группа связанных полей — заполните вопросы ниже.
        </p>
      );
    }

    if (isFileQuestionType(type)) {
      const linkedIds = asStringArray(value);
      const linkedFiles = files.filter((f) => linkedIds.includes(f.file_id));
      return (
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-700 bg-slate-900/60 text-xs text-slate-200 cursor-pointer hover:border-amber-400/70">
            <span>{isUploadingFiles ? 'Загрузка…' : 'Прикрепить файл'}</span>
            <input
              type="file"
              id={fieldId}
              aria-describedby={descriptionId}
              multiple={type !== 'file'}
              accept={MATERIALS_ACCEPT}
              disabled={isUploadingFiles}
              onChange={(e) => handleFileUpload(e, q.id)}
              className="hidden"
            />
          </label>
          {linkedFiles.length > 0 ? (
            <ul className="space-y-1">
              {linkedFiles.map((f) => (
                <li
                  key={f.file_id}
                  className="flex items-center justify-between text-[11px] text-slate-300 bg-slate-800/60 px-2 py-1.5 rounded"
                >
                  <span className="truncate">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(f.file_id)}
                    className="text-rose-400 ml-2"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] text-slate-500">
              Файлы сохраняются в файловое хранилище ({files.length} в заявке)
            </p>
          )}
        </div>
      );
    }

    if (type === 'textarea' || type === 'tool_hierarchy') {
      return (
        <textarea
          id={fieldId}
          aria-describedby={descriptionId}
          rows={type === 'tool_hierarchy' ? 4 : 3}
          value={String(value)}
          onChange={(e) => handleQuestionChange(q.id, e.target.value)}
          placeholder={q.placeholder || (type === 'tool_hierarchy' ? 'Иерархия инструментов / методов' : '')}
          className={inputClass}
        />
      );
    }

    if (type === 'select' || type === 'author_select') {
      const selected = answerText(value);
      return (
        <select
          id={fieldId}
          aria-describedby={descriptionId}
          value={selected}
          onChange={(e) => handleQuestionChange(q.id, e.target.value)}
          className={inputClass}
        >
          <option value="">Выберите...</option>
          {q.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    if (type === 'radio') {
      const selected = answerText(value);
      return (
        <div
          className="rg-answer-options"
          role="radiogroup"
          aria-labelledby={`${fieldId}-label`}
          aria-describedby={descriptionId}
        >
          {q.options.map((opt) => (
            <label key={opt} className="rg-answer-option">
              <input
                type="radio"
                name={fieldId}
                checked={selected === opt}
                onChange={() => handleQuestionChange(q.id, opt)}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    }

    if (MULTI_TYPES.has(type)) {
      const selected = asStringArray(value);
      if (q.options.length === 0) {
        return (
          <textarea
            id={fieldId}
            aria-describedby={descriptionId}
            rows={3}
            value={selected.join('\n')}
            onChange={(e) =>
              handleQuestionChange(
                q.id,
                e.target.value
                  .split('\n')
                  .map((s) => s.trim())
                  .filter(Boolean)
              )
            }
            placeholder={q.placeholder || 'По одному значению на строку'}
            className={inputClass}
          />
        );
      }
      return (
        <div
          role="group"
          aria-labelledby={`${fieldId}-label`}
          aria-describedby={descriptionId}
        >
          <p className="rg-answer-hint">Можно выбрать несколько вариантов</p>
          <div className="rg-answer-options">
            {q.options.map((opt) => {
              const checked = selected.includes(opt);
              return (
                <label key={opt} className="rg-answer-option">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = checked
                        ? selected.filter((v) => v !== opt)
                        : [...selected, opt];
                      handleQuestionChange(q.id, next);
                    }}
                  />
                  <span>{opt}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    if (type === 'date') {
      return (
        <input
          id={fieldId}
          aria-describedby={descriptionId}
          type="date"
          value={String(value)}
          onChange={(e) => handleQuestionChange(q.id, e.target.value)}
          className={inputClass}
        />
      );
    }

    if (type === 'number' || type === 'range') {
      return (
        <input
          id={fieldId}
          aria-describedby={descriptionId}
          type="number"
          value={String(value)}
          onChange={(e) => handleQuestionChange(q.id, e.target.value)}
          placeholder={q.placeholder || ''}
          className={inputClass}
        />
      );
    }

    if (type === 'url') {
      return (
        <input
          id={fieldId}
          aria-describedby={descriptionId}
          type="url"
          value={String(value)}
          onChange={(e) => handleQuestionChange(q.id, e.target.value)}
          placeholder={q.placeholder || 'https://'}
          className={inputClass}
        />
      );
    }

    if (type === 'email') {
      return (
        <input
          id={fieldId}
          aria-describedby={descriptionId}
          type="email"
          value={String(value)}
          onChange={(e) => handleQuestionChange(q.id, e.target.value)}
          placeholder={q.placeholder || ''}
          className={inputClass}
        />
      );
    }

    return (
      <input
        id={fieldId}
        aria-describedby={descriptionId}
        type="text"
        value={Array.isArray(value) ? value.join(', ') : String(value)}
        onChange={(e) => handleQuestionChange(q.id, e.target.value)}
        placeholder={q.placeholder || ''}
        className={inputClass}
      />
    );
  };

  const renderQuestionMeta = (q: ResearchQuestion) => {
    const help = q.help_text || q.comment;
    const type = String(q.type || 'text').toLowerCase();
    const isChoice = type === 'radio' || (MULTI_TYPES.has(type) && q.options.length > 0);
    const fieldId = `rg-q-${q.id}`;
    const label = (
      <>
        {q.label}
        {isQuestionRequired(q, questionAnswers, catalogQuestions) ? ' *' : ''}
      </>
    );
    return (
      <>
        {isChoice ? (
          <div id={`${fieldId}-label`} className="rg-question-label">
            {label}
          </div>
        ) : (
          <label id={`${fieldId}-label`} htmlFor={fieldId} className="rg-question-label">
            {label}
          </label>
        )}
        {help && <p id={`${fieldId}-help`} className="rg-question-help">{help}</p>}
      </>
    );
  };

  const topBackLabel =
    step === firstLogicalStep && !(step === 1 && !isClient && selectedDirection)
      ? '← Вернуться к выбору маршрута'
      : '← Назад';

  return (
    <div
      className="rg-overlay rg-wizard"
      role="dialog"
      aria-modal="true"
      aria-label="Научно-исследовательские услуги"
      data-rg-module="U149_RESEARCH"
    >
      <div className="rg-shell rg-wizard-shell">
        <div className="rg-top">
          <div className="rg-top-left">
            <button type="button" onClick={handleGoBack} className="rg-btn">
              {topBackLabel}
            </button>
            <div>
              <small>Создание заявки</small>
              <h2>Создать заявку</h2>
              <p>
                Каталог услуг · {catalog?.directions_count ?? 11} направлений ×{' '}
                {catalog?.services_count ?? 72} услуг
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 hidden sm:inline">{saveStatus}</span>
            <button type="button" className="rg-btn" onClick={onClose}>
              Закрыть ×
            </button>
          </div>
        </div>

        <div className="rg-wizard-steps">
          {visibleSteps.map((s) => {
            const isActive = step === s.num;
            const isCompleted = step > s.num;
            return (
              <button
                key={s.num}
                type="button"
                onClick={() => {
                  if (s.num <= step) {
                    setStep(s.num);
                    handleAutosave(s.num);
                  }
                }}
                className={`rg-wizard-step${isActive ? ' is-active' : ''}${
                  !isActive && !isCompleted ? ' opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <small>Этап {s.num}</small>
                <strong>{s.title}</strong>
              </button>
            );
          })}
        </div>

        <div className="rg-wizard-body">
          {step === 0 && hasStageZero && (
            <div>
              <div className="rg-panel rg-panel-accent mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-12 rounded bg-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">
                      Как вы хотите начать работу?
                    </h3>
                    <p className="text-xs text-slate-300 max-w-2xl leading-relaxed">
                      Этот управленческий экран доступен только владельцу системы и операционному
                      партнёру. Остальные роли сразу начинают с выбора направления.
                    </p>
                  </div>
                </div>
                <div className="rg-chip-row shrink-0">
                  <span className="rg-chip is-amber">{displayRole}</span>
                  <span className="rg-chip">11 направлений</span>
                  <span className="rg-chip">72 услуг</span>
                </div>
              </div>

              <div className="rg-choice-grid">
                <div
                  onClick={() => {
                    setStartMode('SCRATCH');
                    setStep(1);
                    handleAutosave(1);
                  }}
                  className="rg-choice-card"
                >
                  <div>
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold text-cyan-400 mb-4"
                      style={{ backgroundColor: '#0f2942', border: '1px solid #0369a1' }}
                    >
                      +
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-400 mb-1.5 block">
                      Новая заявка
                    </span>
                    <h4 className="text-lg font-bold text-white group-hover:text-cyan-300 transition mb-2">
                      Новая заявка с нуля
                    </h4>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      Пройти полный маршрут самостоятельно: направление → услуга → сведения →
                      материалы → предварительные ответы → проверка.
                    </p>
                  </div>
                  <div className="rg-choice-cta">Начать с нуля →</div>
                </div>

                <div className="rg-choice-card is-amber">
                  <div>
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold text-amber-400 mb-4"
                      style={{ backgroundColor: '#2e2010', border: '1px solid #d97706' }}
                    >
                      ↓
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-400 mb-1.5 block">
                      Из клиентской заявки
                    </span>
                    <h4 className="text-lg font-bold text-white mb-2">Взять из заявки клиента</h4>
                    <p className="text-xs text-slate-300 leading-relaxed mb-4">
                      Найти уже отправленную форму по коду заявки или выбрать запись из базы.
                      Ответы клиента подставляются в черновик с сохранением источника.
                    </p>

                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="text"
                        value={reqCodeInput}
                        onChange={(e) => setReqCodeInput(e.target.value)}
                        placeholder="REQ-2026-0048"
                        className="flex-1 px-3.5 py-2.5 rounded-xl text-xs font-mono bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-400"
                      />
                      <button
                        type="button"
                        onClick={handleLookupClient}
                        className="px-4 py-2.5 rounded-xl text-xs font-bold text-amber-300 bg-slate-800 hover:bg-slate-700 border border-amber-500/40 transition whitespace-nowrap"
                      >
                        Найти ответы
                      </button>
                    </div>

                    {lookupMessage && (
                      <div
                        className={`p-3 rounded-xl text-xs mb-3 font-medium ${
                          isLookupError
                            ? 'bg-rose-950/60 border border-rose-800/80 text-rose-200'
                            : 'bg-emerald-950/60 border border-emerald-800/80 text-emerald-200'
                        }`}
                      >
                        {lookupMessage}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleOpenClientAnswersModal}
                      className="text-xs font-bold text-slate-300 hover:text-white underline underline-offset-4 transition"
                    >
                      Открыть базу ответов
                    </button>
                    {importedProvenance && (
                      <button
                        type="button"
                        onClick={() => {
                          setStep(1);
                          handleAutosave(1);
                        }}
                        className="px-4 py-2 rounded-xl text-xs font-bold text-slate-950 bg-amber-400 hover:bg-amber-300 transition"
                      >
                        Продолжить с ответами →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              {importedProvenance && (
                <div className="mb-4 p-3.5 rounded-xl bg-amber-400/10 border border-amber-400/40 text-amber-200 text-xs flex items-center justify-between">
                  <span>
                    Импортированы ответы клиента:{' '}
                    <strong>{importedProvenance.request_number}</strong> ({projectTitle})
                  </span>
                  <span className="text-[11px] opacity-80">Импорт из клиентской заявки</span>
                </div>
              )}

              {isClient ? (
                <div>
                  <div className="mb-4">
                    <h3 className="text-base font-bold text-white mb-1">
                      Выберите категорию вашего запроса:
                    </h3>
                    <p className="text-xs text-slate-400">
                      Система автоматически направит задачу профильному академическому специалисту.
                    </p>
                  </div>
                  <div className="rg-service-grid">
                    {(catalog?.client_categories || []).map((cat) => (
                      <div
                        key={cat.category_id}
                        onClick={() => handleSelectClientCategory(cat.category_id)}
                        className={`rg-service-card${
                          selectedClientCategory === cat.category_id ? ' is-selected-amber' : ''
                        }`}
                      >
                        <div>
                          <h4>{cat.title}</h4>
                          <p>{cat.description}</p>
                        </div>
                        <div className="rg-service-cta" style={{ color: '#e2ad3a' }}>
                          Выбрать категорию →
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : !selectedDirection ? (
                <div>
                  <div className="rg-step-head mb-4">
                    <h3>Выберите направление</h3>
                    <p>Сначала направление, затем услуга из этого направления.</p>
                  </div>
                  <div className="rg-service-grid rg-direction-grid">
                    {(catalog?.directions || []).map((d) => {
                      const count = (catalog?.services || []).filter(
                        (s) => s.direction_id === d.direction_id
                      ).length;
                      return (
                        <div
                          key={d.direction_id}
                          onClick={() => {
                            setSelectedDirection(d.direction_id);
                            setValidationError(null);
                          }}
                          className="rg-service-card rg-direction-card"
                        >
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="rg-service-code">{d.display_code}</span>
                              <span className="text-[10px] text-slate-500">{count} услуг</span>
                            </div>
                            <h4>{d.title}</h4>
                            <p>{d.description}</p>
                          </div>
                          <div className="rg-service-cta">
                            <span>Открыть направление</span>
                            <span>→</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="rg-panel mb-4 flex items-center justify-between gap-3">
                    <div>
                      <span className="text-[10px] uppercase font-bold text-cyan-400 block mb-0.5">
                        Направление {selectedDirectionMeta?.display_code}
                      </span>
                      <h3 className="text-base font-bold text-white">
                        {selectedDirectionMeta?.title || selectedDirection}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedDirection(null)}
                      className="text-xs text-cyan-400 underline hover:text-cyan-300 whitespace-nowrap"
                    >
                      ← Все направления
                    </button>
                  </div>
                  <div className="rg-step-head mb-4">
                    <h3>Выберите услугу</h3>
                    <p>Показаны только услуги выбранного направления.</p>
                  </div>
                  <div className="rg-service-grid">
                    {directionServices.map((srv) => {
                      const isChosen = selectedService?.service_id === srv.service_id;
                      return (
                        <div
                          key={srv.service_id}
                          onClick={() => handleSelectService(srv)}
                          className={`rg-service-card${isChosen ? ' is-selected' : ''}`}
                        >
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="rg-service-code">{srv.display_code}</span>
                            </div>
                            <h4>{srv.title}</h4>
                            <p>{srv.description}</p>
                          </div>
                          <div className="rg-service-cta">
                            <span>Выбрать →</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="rg-panel flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold text-cyan-400 block mb-0.5">
                    Выбранная услуга
                  </span>
                  <h3 className="text-base font-bold text-white">
                    {selectedService
                      ? `${selectedService.display_code} · ${selectedService.title}`
                      : 'Услуга не выбрана'}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-xs text-cyan-400 underline hover:text-cyan-300"
                >
                  Изменить услугу
                </button>
              </div>

              {questionsLoading && (
                <div className="rg-panel text-xs text-slate-400">Загрузка вопросов каталога...</div>
              )}

              {!questionsLoading && catalogQuestions.length === 0 && (
                <div className="rg-panel text-xs text-amber-300">
                  Для выбранной услуги пока нет опубликованных вопросов каталога.
                </div>
              )}

              {questionBlocks.map(({ block, title, questions }) => (
                <div key={block} className="rg-panel">
                  <h4 className="rg-section-title">
                    Блок {block}: {title}
                  </h4>
                  <div className="space-y-3.5">
                    {questions.map((q) => (
                      <div key={q.id} className="rg-question">
                        {renderQuestionMeta(q)}
                        {renderQuestionField(q)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="rg-step-head">
                <h3>Материалы</h3>
                <p>
                  Загрузите файлы с проверкой типа и размера, назначьте их вопросам услуги и
                  сохраните состояние черновика.
                </p>
              </div>

              <div
                className={`rg-panel${isDraggingFiles ? ' ring-1 ring-amber-400/60' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDraggingFiles(true);
                }}
                onDragLeave={() => setIsDraggingFiles(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingFiles(false);
                  const dropped = Array.from(e.dataTransfer.files || []);
                  if (dropped.length) void uploadMaterialFiles(dropped);
                }}
              >
                <label className="border-2 border-dashed border-slate-700 hover:border-amber-400/80 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer transition bg-slate-900/40">
                  <span className="text-xs font-semibold text-slate-200">
                    Нажмите для загрузки файлов или перетащите их сюда
                  </span>
                  <span className="text-[11px] text-slate-400 mt-1">
                    DOCX, PDF, XLSX, ZIP, изображения · до 50 МБ на файл
                  </span>
                  <input
                    type="file"
                    multiple
                    accept={MATERIALS_ACCEPT}
                    disabled={isUploadingFiles}
                    onChange={(e) => handleFileUpload(e)}
                    className="hidden"
                  />
                </label>
                {isUploadingFiles && (
                  <p className="text-[11px] text-amber-300 mt-2">Загрузка в файловое хранилище…</p>
                )}

                {files.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <span className="text-xs font-bold text-slate-300">
                      Прикреплённые файлы ({files.length}):
                    </span>
                    <div className="grid grid-cols-1 gap-2">
                      {files.map((f) => (
                        <div
                          key={f.file_id}
                          className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg bg-slate-800/70 border border-slate-700 text-xs"
                        >
                          <span className="truncate max-w-[220px] text-slate-200">{f.name}</span>
                          <div className="flex items-center gap-2">
                            {materialsQuestions.length > 0 && (
                              <select
                                value={f.assigned_to || ''}
                                onChange={(e) => handleAssignFile(f.file_id, e.target.value)}
                                className="px-2 py-1 rounded bg-slate-900 border border-slate-600 text-[11px] text-slate-200"
                                aria-label={`Назначение файла ${f.name}`}
                              >
                                <option value="">Без назначения</option>
                                {materialsQuestions.map((q) => (
                                  <option key={q.id} value={q.id}>
                                    {q.label}
                                  </option>
                                ))}
                              </select>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveFile(f.file_id)}
                              className="text-rose-400 hover:text-rose-300 font-bold"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {materialsQuestions.length > 0 && (
                <div className="space-y-3">
                  <h4 className="rg-section-title">Файловые поля услуги</h4>
                  {materialsQuestions.map((q) => (
                    <div key={q.id} className="rg-panel">
                      {renderQuestionMeta(q)}
                      {renderQuestionField(q)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <div className="rg-step-head">
                <h3>Предварительные ответы</h3>
                <p>
                  Для каждой записи доступны принять, изменить, очистить и восстановить. Конфликты
                  нужно закрыть до финальной проверки.
                </p>
              </div>

              {preliminaryQuestions.length === 0 && preliminaryAnswers.length === 0 && (
                <div className="rg-panel text-xs text-slate-400">
                  Для выбранной услуги нет предварительных ответов. Можно продолжить.
                </div>
              )}

              {preliminaryConflicts.length > 0 && (
                <div className="rg-panel text-xs text-amber-200 border border-amber-500/40">
                  Конфликтов: {preliminaryConflicts.length}. Проверьте и подтвердите ответы, требующие уточнения.
                </div>
              )}

              <div className="space-y-3">
                {preliminaryQuestions.map((q) => {
                  const status = getPreliminaryStatus(q, questionAnswers, prelimDecisions[q.id]);
                  return (
                    <div key={q.id} className="rg-panel">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h4 className="text-xs font-semibold text-slate-200">{q.label}</h4>
                        <div className="flex flex-wrap gap-1.5 justify-end">
                          {q.preliminary_source && (
                            <span className="rg-chip shrink-0">Предварительный ответ</span>
                          )}
                          <span className="rg-chip shrink-0">{{ EMPTY: 'Нет ответа', PREFILLED: 'Предложен', ACCEPTED: 'Принят', EDITED: 'Изменён', CLEARED: 'Очищен', CONFLICT: 'Требует уточнения' }[status]}</span>
                        </div>
                      </div>
                      {q.preliminary_answer && (
                        <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                          Предварительно: {q.preliminary_answer}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button
                          type="button"
                          className="rg-btn"
                          onClick={() => handleAcceptPreliminary(q)}
                        >
                          Принять
                        </button>
                        <button
                          type="button"
                          className="rg-btn"
                          onClick={() => handleClearPreliminary(q)}
                        >
                          Очистить
                        </button>
                        <button
                          type="button"
                          className="rg-btn"
                          onClick={() => handleRestorePreliminary(q)}
                        >
                          Восстановить
                        </button>
                      </div>
                      <p className="rg-question-help">Подтверждённый ответ</p>
                      {renderQuestionMeta(q)}
                      {renderQuestionField(q)}
                    </div>
                  );
                })}

                {preliminaryQuestions.length === 0 &&
                  preliminaryAnswers.map((pa, idx) => (
                    <div key={idx} className="rg-panel text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-slate-300">{pa.question}</span>
                        <span className="rg-chip">Клиентский ответ</span>
                      </div>
                      <p className="text-slate-400">{pa.answer}</p>
                    </div>
                  ))}
              </div>

              <div className="rg-panel" role="status">
                {reviewPending ? 'Проверяем заявку…' : reviewMode === 'openai' ? 'Проверка выполнена ИИ' : 'Проверка выполнена по правилам; OpenAI не использован'}
                {reviewError && <div role="alert">{reviewError} <button type="button" className="rg-btn" onClick={() => setReviewRetry((value) => value + 1)}>Повторить проверку</button></div>}
              </div>
              {clarifications.length > 0 && (
                <div className="rg-panel space-y-3">
                  <h4 className="rg-section-title">Уточняющие вопросы</h4>
                  {clarifications.map((cl) => (
                    <div key={cl.id}>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                        {cl.question}
                        {cl.blocking ? ' *' : ''}
                      </label>
                      <textarea
                        rows={2}
                        value={clarificationAnswers[cl.id] || ''}
                        onChange={(e) => {
                          setClarificationAnswers((prev) => ({
                            ...prev,
                            [cl.id]: e.target.value,
                          }));
                          setValidationError(null);
                        }}
                        className={inputClass}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <div>
              {submitResult ? (
                <div className="rg-success">
                  <div className="rg-success-mark">✓</div>
                  <h3>Заявка успешно создана</h3>
                  <p>
                    Номер заявки: <strong>{submitResult.reference_code}</strong>. Запрос
                    зарегистрирован и закреплён за вашей учётной записью.
                  </p>
                  <div style={{ marginTop: 18 }}>
                    <button type="button" onClick={onClose} className="rg-btn primary">
                      Закрыть
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="rg-step-head">
                    <h3>Проверка</h3>
                    <p>Сводка по услуге, ответам и материалам перед созданием заявки.</p>
                  </div>

                  <div className="rg-summary">
                    <div className="rg-summary-row">
                      <span>Услуга</span>
                      <span>
                        {selectedService?.display_code} · {selectedService?.title}
                      </span>
                    </div>
                    <div className="rg-summary-row">
                      <span>Направление</span>
                      <span>
                        {selectedDirectionMeta?.display_code} · {selectedDirectionMeta?.title}
                      </span>
                    </div>
                    {preliminaryConflicts.length > 0 && (
                      <div className="rg-summary-row text-amber-300">
                        <span>Конфликты предварительных</span>
                        <span>{preliminaryConflicts.length} — создание заблокировано</span>
                      </div>
                    )}
                    {missingRequired.length > 0 && (
                      <div className="rg-panel text-xs text-rose-200 border border-rose-500/40 mb-3">
                        <strong className="block mb-1">
                          Обязательные пропуски ({missingRequired.length}):
                        </strong>
                        <ul className="list-disc pl-4 space-y-0.5">
                          {missingRequired.slice(0, 8).map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                          {missingRequired.length > 8 ? (
                            <li>…и ещё {missingRequired.length - 8}</li>
                          ) : null}
                        </ul>
                      </div>
                    )}
                    {visibleQuestions
                      .filter((q) => !isFileQuestionType(q.type))
                      .map((q) => {
                        const raw = questionAnswers[q.id];
                        const display = Array.isArray(raw)
                          ? raw.join(', ')
                          : answerText(raw) || '—';
                        return (
                          <div key={q.id} className="rg-summary-row">
                            <span>{q.label}</span>
                            <span>{display}</span>
                          </div>
                        );
                      })}
                    <div className="rg-summary-row">
                      <span>Прикреплено файлов</span>
                      <span>{files.length}</span>
                    </div>
                    {files.map((f) => (
                      <div key={f.file_id} className="rg-summary-row">
                        <span>Файл{f.assigned_to ? ' · назначен' : ''}</span>
                        <span>{f.name}</span>
                      </div>
                    ))}
                  </div>

                  <label className="rg-consent">
                    <input
                      type="checkbox"
                      checked={declarationConfirmed}
                      onChange={(e) => {
                        setDeclarationConfirmed(e.target.checked);
                        setValidationError(null);
                      }}
                    />
                    <span>
                      Подтверждаю достоверность сведений, отсутствие нарушений академической
                      честности и согласие на обработку данных исследования.
                    </span>
                  </label>

                  <button
                    type="button"
                    disabled={
                      !declarationConfirmed ||
                      isSubmitting ||
                      preliminaryConflicts.length > 0 ||
                      missingRequired.length > 0
                    }
                    onClick={handleSubmit}
                    className="rg-btn primary"
                    style={{ width: '100%', minHeight: 44 }}
                  >
                    {isSubmitting ? 'Создание…' : 'Создать заявку'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {!submitResult && (
          <div
            className="px-6 py-4 border-t shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3"
            style={{ borderColor: '#1e293b', backgroundColor: '#090f1b' }}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => handleAutosave(step)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 transition"
              >
                Сохранить черновик
              </button>
              <button
                type="button"
                onClick={handleExitAndContinueLater}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 transition"
              >
                Выйти и продолжить позже
              </button>
            </div>

            <div className="flex items-center gap-3 justify-end">
              {validationError && (
                <span className="text-xs text-rose-300 max-w-xs truncate">{validationError}</span>
              )}
              {(step > firstLogicalStep || (step === 1 && !isClient && selectedDirection)) && (
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 transition"
                >
                  ← Назад
                </button>
              )}

              {step < 5 && (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="px-5 py-2 rounded-xl text-xs font-bold text-slate-950 bg-amber-400 hover:bg-amber-300 transition"
                >
                  Продолжить →
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {showClientAnswersModal && (
        <div className="cab-overlay" onClick={() => setShowClientAnswersModal(false)}>
          <div
            className="relative w-full max-w-2xl rounded-2xl p-6 border shadow-2xl text-slate-100 max-h-[80vh] flex flex-col"
            style={{ backgroundColor: '#0c1322', borderColor: '#1e293b' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-800">
              <h3 className="text-base font-bold text-white">Реестр клиентских ответов</h3>
              <button
                type="button"
                onClick={() => setShowClientAnswersModal(false)}
                className="text-slate-400 hover:text-white font-bold"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto space-y-2.5 flex-1 pr-1">
              {clientAnswersList.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSelectClientRecord(item)}
                  className="p-3.5 rounded-xl border border-slate-800 bg-slate-900/70 hover:border-amber-400 hover:bg-slate-800/80 cursor-pointer transition"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-mono font-bold text-amber-300">
                      {item.request_number}
                    </span>
                    <span className="text-[10px] text-slate-400">{item.created_at.slice(0, 10)}</span>
                  </div>
                  <h5 className="text-xs font-semibold text-white mb-1">{item.title}</h5>
                  <p className="text-[11px] text-slate-400">{item.service_type}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
